import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { connect } from 'react-redux';
import { Header, Divider } from 'semantic-ui-react';
import { DateLabel, Score, Table } from 'components';
import { translateBenchmarkHeading } from '../../../../utils/helpers';
import { translation } from '../../../Translation/scorecard';

const TABLE_STYLE = {
  tableLayout: 'fixed',
  width: '100%',
};

const TABLE_COLUMN_WIDTHS = [
  '18.75%',
  '20.3125%',
  '20.3125%',
  '20.3125%',
  '20.3125%',
];

export const hasVaRValue = (num) =>
  num !== null && num !== undefined && !Number.isNaN(num);

export const hasVaRData = (data) => {
  const VaR =
    data &&
    data.fratios &&
    data.fratios.portfolio &&
    data.fratios.portfolio.VaR;

  return Boolean(
    VaR && ['p1', 'p5', 'p10'].some((key) => hasVaRValue(VaR[key]))
  );
};

export const formatVaRAbs = (num) =>
  hasVaRValue(num) ? (Math.abs(num) * 100).toFixed(2) : '-';

@connect((state) => ({
  french: state.Storage.language === 'fr',
}))
export default class Downside extends Component {
  getPercent = (num) => (num ? `${(num * 100).toFixed(2)} %` : '-');

  getDownside = (num) => (num ? `${(num * 100).toFixed(2)} %` : '0%');

  getVaR = (num) => (this.hasValue(num) ? `${(num * 100).toFixed(2)} %` : '-');
  getVaRAbs = formatVaRAbs;
  hasValue = hasVaRValue;

  parseDate = (portfolio) => {
    const init = portfolio.init_date.split('-');
    const end = portfolio.end_date.split('-');
    const { french } = this.props;
    const language = french ? 'fr' : 'en';

    return `${translation[language].MONTH[[init[1]]]} ${init[0]} ${
      french ? 'à' : 'to'
    } ${translation[language].MONTH[[end[1]]]} ${end[0]}`;
  };

  renderVaR = () => {
    const { data } = this.props.analytics;
    const { french } = this.props;
    const VaR =
      data.fratios && data.fratios.portfolio && data.fratios.portfolio.VaR;

    // Only display when at least one VaR value is returned
    if (!hasVaRData(data)) {
      return null;
    }

    const columns = [
      {
        key: 'p1',
        label: french ? 'VaR à 1 %' : '1% VaR',
        pct: 1,
        freq: 100,
      },
      {
        key: 'p5',
        label: french ? 'VaR à 5 %' : '5% VaR',
        pct: 5,
        freq: 20,
      },
      {
        key: 'p10',
        label: french ? 'VaR à 10 %' : '10% VaR',
        pct: 10,
        freq: 10,
      },
    ];

    const p5Example = this.hasValue(VaR.p5) ? this.getVaRAbs(VaR.p5) : '5';

    return (
      <React.Fragment>
        <Header style={{ marginTop: '2rem' }}>
          <Header.Content>
            {french ? 'Valeur à risque' : 'Value-at-Risk'}
          </Header.Content>
          <Header.Subheader>
            {french
              ? 'Perte minimale estimée sur un mois à chaque niveau de probabilité'
              : 'Estimated minimum one-month loss at each probability level'}
          </Header.Subheader>
          <Header.Subheader>
            {french
              ? 'Selon les rendements mensuels des 10 dernières années'
              : 'Based on the past 10 years of monthly returns'}
          </Header.Subheader>
        </Header>

        <Divider />

        <Table basic textAlign="center" style={TABLE_STYLE}>
          <colgroup>
            {TABLE_COLUMN_WIDTHS.map((width, index) => (
              <col key={index} style={{ width }} />
            ))}
          </colgroup>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell />
              {columns.map((col) => (
                <Table.HeaderCell key={col.key}>{col.label}</Table.HeaderCell>
              ))}
              <Table.HeaderCell />
            </Table.Row>
          </Table.Header>
          <Table.Body>
            <Table.Row positive>
              <Table.Cell
                width={3}
                style={{
                  textAlign: 'center',
                  paddingLeft: 0,
                }}
              >
                {french ? 'Mon portefeuille' : 'My Portfolio'}
              </Table.Cell>
              {columns.map((col) => (
                <Table.Cell key={col.key}>
                  {this.getVaR(VaR[col.key])}
                </Table.Cell>
              ))}
              <Table.Cell />
            </Table.Row>
          </Table.Body>
        </Table>
      </React.Fragment>
    );
  };

  render() {
    const { analytics } = this.props;
    const { benchmark, data } = analytics;
    const { french } = this.props;
    const language = french ? 'fr' : 'en';

    return (
      <Container>
        <Score
          name={`${translation[language].DOWNSIDE} `}
          score={`${data.score.Volatility[benchmark][0]}%`}
          grade={data.score.Volatility[benchmark][1]}
          portfolio={data.portfolio}
          pdf
          extraInfo={
            french
              ? 'Par rapport à votre indice de référence'
              : 'Relative to your benchmark'
          }
        />
        {french ? (
          <div>
            <p>
              La protection contre les baisses découle de la capacité de votre
              portefeuille à limiter les pertes en cas de baisse du cours du
              marché.
            </p>
            <p>
              En gestion de portefeuille, la mesure traditionnelle du risque (ou
              de la volatilité) est l’écart-type des rendements historiques d’un
              portefeuille. Il s’agit d’une mesure du risque total du
              portefeuille. Il est présenté sous forme de pourcentage annualisé
              pour faciliter la comparaison, peu importe le portefeuille ou la
              période.
            </p>
            <p>
              Il est important de noter que le risque total comprend la
              possibilité de gains et de pertes. Au fil du temps, les rendements
              du portefeuille varieront au-dessus de la moyenne (ou de votre
              objectif de rendement), mais aussi en dessous. Par instinct, et
              aussi car c’est généralement un comportement rationnel, nous avons
              tendance à nous préoccuper davantage du risque de pertes que du
              risque de gains
            </p>
          </div>
        ) : (
          <div>
            <p>
              Downside protection speaks to your portfolio's ability to limit
              losses when there is a market downturn.
            </p>
            <p>
              In portfolio management, the traditional measure of risk (or
              volatility) is the standard deviation of a portfolio’s historical
              returns. It is a measure of the portfolio's <strong>total</strong>{' '}
              risk. It is reported as an annualized percentage for easy
              comparison across portfolios and time periods.
            </p>
            <p>
              It is important to note that total risk has an upside and a{' '}
              <strong>downside</strong>. Over time, portfolio returns will
              fluctuate above the average (or your target return), as well as{' '}
              <strong>below</strong>. It is human nature and often rational to
              be more concerned about downside risk than upside risk.
            </p>
          </div>
        )}

        <Header>
          <Header.Content>
            {translation[language].KEY_STATISTICS}
          </Header.Content>
          <Header.Subheader>
            <DateLabel
              str={`${this.parseDate(data.portfolio)} (${
                translation[language].INCLUSIVE
              })`}
            />
          </Header.Subheader>
        </Header>

        <Divider />

        <Table basic textAlign="center" style={TABLE_STYLE}>
          <colgroup>
            {TABLE_COLUMN_WIDTHS.map((width, index) => (
              <col key={index} style={{ width }} />
            ))}
          </colgroup>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell />
              <Table.HeaderCell>
                {translation[language].TOTAL_RISK}
              </Table.HeaderCell>
              <Table.HeaderCell>
                {translation[language].DOWNSIDE_RISK}
              </Table.HeaderCell>
              <Table.HeaderCell>
                {translation[language].MAXIMUM_DRAWDOWN}
              </Table.HeaderCell>
              <Table.HeaderCell>
                {translation[language].DOWNSIDE_CAPTURE}
              </Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            <Table.Row positive>
              <Table.Cell>{translation[language].MY_PORTFOLIO}</Table.Cell>
              <Table.Cell>
                {this.getPercent(data.fratios.portfolio.stdDev)}
              </Table.Cell>
              <Table.Cell>
                {this.getDownside(data.fratios.portfolio.dsr)}
              </Table.Cell>
              <Table.Cell>
                {this.getDownside(data.fratios.portfolio.maxDd)}
              </Table.Cell>
              <Table.Cell>
                {this.getPercent(data.fratios.portfolio.dscapture[benchmark])}
              </Table.Cell>
            </Table.Row>
            <Table.Row>
              <Table.Cell width={3}>
                {translateBenchmarkHeading(
                  data.fratios[benchmark].name,
                  french
                )}
              </Table.Cell>
              <Table.Cell>
                {this.getPercent(data.fratios[benchmark].stdDev)}
              </Table.Cell>
              <Table.Cell>
                {this.getPercent(data.fratios[benchmark].dsr)}
              </Table.Cell>
              <Table.Cell>
                {this.getPercent(data.fratios[benchmark].maxDd)}
              </Table.Cell>
              <Table.Cell>
                {this.getPercent(data.fratios[benchmark].dscapture)}
              </Table.Cell>
            </Table.Row>
          </Table.Body>
        </Table>
        {this.renderVaR()}
      </Container>
    );
  }
}

Downside.propTypes = {
  analytics: PropTypes.object.isRequired,
  french: PropTypes.bool.isRequired,
};

const Container = styled.div`
  min-height: 100%;
  padding: 2rem 2rem 1rem 2rem;
`;
